import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useCreateOrUpdateDiscount } from '~entities/discount/api/create-update-discount';
import { useGetDiscount } from '~entities/discount/api/get-discount';

import { useGetCurrency } from '~shared/api/currency-api';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';

import { DiscountRulesSelector } from './discount-rules';
import { ExcludedClassesTable } from './excluded-classes-table';

type DiscountType = 'percent' | 'dollars';

interface ClassDiscount {
  id: number;
  value: string;
}

// Helper function to get the button class based on selection state
const getButtonClass = (isSelected: boolean) => {
  const baseClass =
    'relative block rounded-md px-4 py-2 text-sm font-medium cursor-pointer transition-colors';
  if (isSelected) {
    return `${baseClass} bg-yellow-300 text-gray-900 shadow`;
  } else {
    return `${baseClass} text-gray-500 hover:bg-gray-100 hover:text-gray-900`;
  }
};

export function MultiClassDiscount({
  discountRules,
  setDiscountRules,
}: {
  discountRules: string | null;
  setDiscountRules: (rules: string | null) => void;
}) {
  const { data: discountData } = useGetDiscount('multi-class');
  const { data: currencyDetails } = useGetCurrency();
  const updateDiscountMutation = useCreateOrUpdateDiscount();
  const [isActive, setIsActive] = useState(false);
  const [discountType, setDiscountType] = useState<DiscountType>('percent');
  const [classDiscounts, setClassDiscounts] = useState<Array<ClassDiscount>>([
    { id: 1, value: '0' },
  ]);

  const [classExcludedClasses, setClassExcludedClasses] = useState<
    Array<string>
  >([]);

  useEffect(() => {
    if (discountData) {
      setIsActive(discountData.isActive);
      setDiscountType(discountData.type as DiscountType);
      setDiscountRules(discountData.discountRules);
      setClassExcludedClasses(discountData.excludedClasses ?? []);
      if (discountData.discounts) {
        const discounts = Object.entries(discountData.discounts).map(
          ([id, value]) => ({
            id: Number(id),
            value: value ? value.toString() : '0',
          }),
        );
        setClassDiscounts(discounts);
      }
    }
  }, [discountData, setDiscountRules]);

  const handleAddMore = () => {
    const nextId = classDiscounts.length + 1;
    setClassDiscounts([...classDiscounts, { id: nextId, value: '0' }]);
  };

  const handleDiscountChange = (id: number, value: string) => {
    setClassDiscounts((prevDiscounts) =>
      prevDiscounts.map((discount) =>
        discount.id === id ? { ...discount, value } : discount,
      ),
    );
  };

  const handleRemoveDiscount = (id: number) => {
    setClassDiscounts((prevDiscounts) => {
      // First filter out the removed discount
      const filteredDiscounts = prevDiscounts.filter(
        (discount) => discount.id !== id,
      );

      // Then reindex the remaining discounts
      return filteredDiscounts.map((discount, index) => ({
        ...discount,
        id: index + 1, // Reindex starting from 1
      }));
    });
  };

  const handleSubmit = () => {
    if (!discountRules) {
      toast.error('Please select discount rules');
      return;
    }
    const discountDataPayload = {
      type: discountType,
      excludedClasses: classExcludedClasses,
      discountRules: discountRules,
      category: 'multi-class',
      isActive,
      discounts: classDiscounts.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.id]: isNaN(Number(curr.value)) ? 0 : Number(curr.value),
        }),
        {},
      ),
    };
    if (discountData && Object.keys(discountData).length > 0) {
      updateDiscountMutation.mutate({
        ...discountDataPayload,
        action: 'update',
      });
    } else {
      updateDiscountMutation.mutate({
        ...discountDataPayload,
        action: 'create',
      });
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        {/* Discount Type Selection */}
        <div className='space-y-4'>
          <label className='block text-sm font-medium text-gray-700'>
            Discount Type
          </label>
          <RadioGroup
            value={discountType}
            onValueChange={(value) => {
              setDiscountType(value as DiscountType);
            }}
            className='inline-flex rounded-lg bg-gray-50 p-1 shadow-sm'
          >
            {[
              { value: 'percent', label: '% Percentage' },
              { value: 'dollars', label: '$ Amount' },
            ].map((option) => (
              <div className='relative' key={option.value}>
                <RadioGroupItem
                  value={option.value}
                  id={option.value}
                  className='sr-only'
                />
                <label
                  htmlFor={option.value}
                  className={getButtonClass(discountType === option.value)}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Active Toggle */}
        <div className='inline-flex items-center justify-between rounded-lg border bg-white p-3 shadow-sm'>
          <div className='flex items-center space-x-3'>
            <div className='flex flex-col'>
              <span className='text-sm text-gray-500'>
                {isActive
                  ? 'Multi-Class Discount is currently active'
                  : 'Multi-Class Discount is currently inactive'}
              </span>
            </div>
          </div>
          <div className='ml-8'>
            <button
              type='button'
              onClick={() => {
                setIsActive(!isActive);
              }}
              className={`relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                isActive ? 'bg-green-500' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block size-7 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isActive ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Organization Info */}
      <div className='flex items-center text-sm text-gray-600'>
        <span>
          Your organization is set to count classes for multi-class discounting
          by Student
        </span>
      </div>

      {/* Discount Configuration */}
      <div className='space-y-2'>
        <label className='block text-sm font-medium text-gray-700'>
          What are the discounts in{' '}
          {discountType === 'percent' ? '%' : (currencyDetails?.symbol ?? '$')}?
        </label>
        <div className='max-w-2xl space-y-3'>
          {classDiscounts.map((discount) => (
            <div
              key={discount.id}
              className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:space-x-4'
            >
              <label className='text-sm text-gray-700'>
                Class {discount.id}
              </label>
              <div className='flex items-center space-x-2'>
                <div className='w-full sm:w-[200px]'>
                  <input
                    type='number'
                    value={discount.value}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleDiscountChange(discount.id, value);
                    }}
                    min='0'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-yellow-400 focus:ring-yellow-400 sm:text-sm'
                    placeholder={
                      discountData?.type === 'percent'
                        ? '0%'
                        : `0${currencyDetails?.symbol ?? '$'}`
                    }
                  />
                </div>
                <button
                  onClick={() => {
                    handleRemoveDiscount(discount.id);
                  }}
                  className='self-center hover:text-red-700'
                >
                  <X className='size-4' />
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleAddMore}
          className='mt-4 rounded-md bg-yellow-400 px-4 py-2 text-sm font-medium text-black hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2'
        >
          Add Classes
        </button>
      </div>
      <DiscountRulesSelector
        selectedRules={discountRules}
        setSelectedRules={setDiscountRules}
      />
      <ExcludedClassesTable
        selectedClasses={classExcludedClasses}
        setSelectedClasses={setClassExcludedClasses}
      />
      {/* Submit Button */}
      <div className='flex justify-end'>
        <button
          onClick={handleSubmit}
          className='inline-flex justify-center rounded-md border border-transparent bg-yellow-400 px-4 py-2 text-sm font-medium text-black shadow-sm hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2'
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}
